import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api/api"; 
import EventsTable from "./EventsTable";
import EventModal from "./EventModal";
// Il kit degli strumenti admin PRIMA del foglio della pagina (calco
// dell'ordine di UniformDistribution.jsx): i pulsanti in testa sono
// `qpsc-tool-btn`. Il foglio porta solo classi `.qpsc-*`, nessun selettore nudo.
import "./AdminTools.css";
import "./CalendarClub.css";
// IL TELAIO CONDIVISO (doc 07 §7): barra utente, banda «‹ Back» / «Switch
// role», colonna «admin» a tutta finestra sopra i 768px. Calco di
// UsersList.jsx (cantiere CALENDAR ADMIN, 26/9/2026).
import HomeShell from "./HomeShell";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from 'react-router-dom';
// SEDE UNICA DEI RUOLI (auth/access.js, ri-esportata da roleUtils).
//
// Due domande diverse, due funzioni — decisione 1 dell'8/8/2026:
//   hasRole      = «POSSIEDE il ruolo?»  → i COMANDI (aggiungi, modifica,
//                  cancella). Guarda `role` E i context
//                  ACTIVE, perché un permesso non si perde cambiando vista.
//   isActingAs   = «che cappello indossa ADESSO?» → le VISTE (qui, dal
//                  1/10/2026, le sole schede dei totali: vedi `indossaPlayer`).
// Entrambe tolleranti alle grafie e ai ruoli storici: "Player"/"PLAYER" si
// equivalgono, "Manager"/"TD"/"ManagerTD" valgono COACH, "Child" vale PLAYER.
//
import { hasRole, isActingAs } from "../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA (cantiere «lettore unico», fetta
// F4): di qui arriva, già scritta, l'appartenenza di un evento a una squadra
// (la usa il filtro «Team», E4). `canonicalRole` e `normalizza` non si
// importano più: li applica il modulo, come metro di grafia sui nomi di
// squadra (lo stesso che il backend applica con `normalizzaNomeSquadra`).
// ⛔️ `squadreDelSoggetto` e `primaSquadraDellaTessera` non si importano più:
// servivano solo alla «My Week», tolta (cantiere D7CAL, 1/10/2026).
import { eDiUnaMiaSquadra } from "../utils/squadre";
import { getWeekNumber, getWeekRange } from "../utils/calendario";

function isMobile() {
  return window.innerWidth <= 650;
}

// ⚠️ `evTimeStamp` resta qui: NON è la copia di utils/calendario.js (legge la
// data da sé, senza `dataEvento`). Giro G-K1: si lascia com'è.
function evTimeStamp(ev){
  if(!ev || !ev.date) return 0;
  const t = (ev.time && /^\d{2}:\d{2}$/.test(ev.time)) ? ev.time : "00:00";
  const [hh, mm] = t.split(":").map(Number);

  if (/^\d{4}-\d{2}-\d{2}$/.test(ev.date)) {
    const [y,m,d] = ev.date.split("-").map(Number);
    return new Date(y, m-1, d, hh, mm).getTime();        // <— locale
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(ev.date)) {
    const [d, m, y] = ev.date.split("-").map(Number);
    return new Date(y, m-1, d, hh, mm).getTime();        // <— locale
  }
  return 0;
}

// ===========================================================================
// LE MIE SQUADRE — le copie di pagina NON CI SONO PIÙ (fetta F4), e dal
// 1/10/2026 non c'è più nemmeno la «My Week» che le usava (cantiere D7CAL)
// ===========================================================================
// Qui vivevano `squadreDelCoach`, `squadraDelGiocatore` e `eDiUnaMiaSquadra`,
// scritte dalla fetta C; il cantiere «lettore unico» le ha portate in
// `utils/squadre.js`. Delle tre letture questa pagina ne importa ora UNA
// sola, `eDiUnaMiaSquadra(nomeEvento, nomi)` — corpo identico, confronto
// normalizzato sui DUE lati — per il filtro «Team» della tabella (E4).
// Le altre due (`squadreDelSoggetto`, `primaSquadraDellaTessera`) servivano
// solo alle squadre del cappello della «My Week»: tolta la My Week (decisione
// (a) del committente, registro D-07), non si importano più. Le loro
// differenze volute (D6, D7, D8) vanno rilette in `utils/squadre.js` e nella
// storia di questo file.

export default function CalendarClub() {
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  // FILTRO PER SQUADRA della tabella di club (cantiere CALENDAR ADMIN, E4,
  // decisione del committente del 27/9/2026). `""` = All Teams. Calco dello
  // stato `filterTeam` di UsersList. ⚠️ NON si azzera in `prevWeek`/`nextWeek`:
  // il filtro resta al cambio di settimana (regola 3).
  const [filtroSquadra, setFiltroSquadra] = useState("");

  // RESA — «siamo su telefono?» diventa uno STATO, non una lettura secca.
  // `isMobile()` (in testa al file) era chiamata in fase di RENDER dai due
  // rami della vista: il valore era quello dell'ultimo ridisegno, e nessuno
  // ridisegna la pagina quando la finestra cambia larghezza. Chi ruotava il
  // telefono, o stringeva la finestra sul desktop, restava nella vista
  // sbagliata finché non toccava qualcos'altro (una freccia della settimana,
  // una spunta). Ora la larghezza si ascolta: stesso valore iniziale di
  // prima, stessa soglia, e il listener si toglie allo smontaggio.
  const [mobile, setMobile] = useState(isMobile);
  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { user, activeContext } = useAuth() || {};

  // ⚠️ Qui stavano due `console.log` che stampavano nella console del browser
  // l'INTERO documento utente e l'intero context attivo, a ogni ridisegno del
  // calendario: nome, cognome, email, data di nascita, squadra, contexts. Su
  // un'app che tratta anagrafiche di minori è un'esposizione gratuita, e non
  // doveva arrivare al travaso in produzione. Tolti in P9 (la rimozione era
  // stata segnalata in P1 e rinviata: chiusura P1 dell'8/8/2026).
  // Gli altri log di questo file non sono stati toccati.

  const navigate = useNavigate();
  const delegatedProfile = JSON.parse(
    localStorage.getItem("delegatedUser") || "null"
  );
  const isChildMode = !!delegatedProfile;

  // COMANDO — chi può intervenire sul calendario: aggiungere un evento
  // (pulsante in testa) e modificare o cancellare un evento dalla tabella
  // (prop passata a EventsTable più sotto); dal cantiere CALENDAR ADMIN
  // (26/9/2026) governa anche «Full Club Calendar» in testa. Un solo
  // permesso per tutti, così i pulsanti in testa e la tabella non possono
  // dire cose diverse. L'«Export CSV» della settimana non c'è più (stesso
  // cantiere, decisione 5 del committente).
  //
  // SCRITTURA RISERVATA AGLI ADMIN (cantiere CALENDAR, fetta B). Fino a qui
  // l'elenco diceva anche "COACH", aggiunto in P1: la scrittura sul calendario
  // di club era aperta agli allenatori, per `role` o per context. La fetta A ha
  // chiuso il backend — POST/PUT/DELETE `/events`, più `/events/series` e
  // `/events/import`, chiedono ora `requireRole("ADMIN","SUPERADMIN")` — quindi
  // un allenatore che vedesse ancora questi comandi otterrebbe soltanto un 403:
  // il bottone prometterebbe ciò che il server rifiuta. Tolto "COACH", frontend
  // e backend dicono la stessa cosa.
  //
  // ⚠️ Nessuna VISTA si spegne per questo: le viste dipendono dal CAPPELLO
  // (`indossaPlayer`, qui sotto), mai da questo permesso. Un COACH perde i
  // bottoni di comando («+ Add Event»; allora anche «Export CSV», tolto il
  // 26/9/2026) e matita e cestino sulla tabella di club; tutto ciò che vedeva
  // continua a vederlo. (Dal 1/10/2026, tolta la «My Week», la tabella la
  // vede con ogni cappello: cantiere D7CAL.)
  //
  // Il metro resta il gate unico (`hasRole`), che comprende la grafia storica
  // ("Admin", "SuperAdmin") e chi possiede il ruolo per tessera oltre che per
  // `role` di primo livello. La guardia su `user` non serve: `hasRole` su un
  // utente nullo è falso per costruzione.
  const canEditCalendar = hasRole(user, "SUPERADMIN", "ADMIN");

  // ⛔️ Qui stava `canPlanTeams` (selezione delle squadre e Weekly Planner):
  // tolto con la sezione «Select teams» e il planner (cantiere CALENDAR
  // ADMIN, 26/9/2026, decisione 5 del committente).

  // CAPPELLO — non «cosa può fare», ma «cosa sta facendo adesso»: da questo
  // dipende solo COSA SI VEDE (oggi: le schede dei totali di club, che il
  // cappello PLAYER non vede). Mai un comando. ⛔️ Fino al 1/10/2026 decideva
  // anche la «My Week» al posto della tabella, e qui stava `indossaCoach`:
  // tolti entrambi (cantiere D7CAL, registro D-07).
  //
  // Il cappello è il ruolo dell'`activeContext`; chi non ne ha uno indossa il
  // proprio `role` di primo livello. Un allenatore che sta guardando l'app da
  // giocatore vede la pagina da giocatore, ma i comandi da allenatore
  // restano suoi (canEditCalendar qui sopra).
  //
  // ⚠️ IL LIMITE NOTO DI P1 È CHIUSO (F6): lo Switch di HomeContext non si
  // limita più a navigare, IMPOSTA l'activeContext col ruolo scelto (tessera
  // attiva corrispondente, o cappello sintetico per il `role` di primo
  // livello). Le viste qui sotto seguono quindi la scelta della persona e non
  // più il PRIMO context ACTIVE dell'ingresso. Il metro resta quello di
  // sempre — access.js, `currentRole`/`isActingAs` — e chi ha il solo `role`
  // non è toccato, perché non ha cappelli fra cui scegliere.
  const indossaPlayer = isActingAs(user, activeContext, "PLAYER");

  //  INVARIATO: Carica eventi e teams 
  const loadEvents = useCallback(async () => {
    try {
      const data = await api.get("/events");
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load events:", e);
      setEvents([]);
    }
  }, []);

  const loadTeams = async () => {
    try {
      const data = await api.get("/teams");
      // ⚠️ Qui stava `console.log("Teams dal backend:", data)`: l'elenco delle
      // squadre del club finiva nella console del browser a ogni apertura del
      // calendario. Non è un'anagrafica, ma è un residuo di sviluppo acceso, e
      // non ha ragione di restare (fetta D). I due `console.error` dei
      // `catch` restano dove sono: quelli segnalano un guasto vero.
      setTeams(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load teams:", e);
      setTeams([]);
    }
  };

  useEffect(() => { loadTeams(); }, []);
  useEffect(() => { loadEvents(); }, [currentWeek, loadEvents]);

  // Settimana corrente
  function prevWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() - 7);
    setCurrentWeek(d);
  }
  function nextWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() + 7);
    setCurrentWeek(d);
  }

  // FILTRO EVENTI PER SETTIMANA
  //  Settimana corrente: range e numero 
  const [weekStartStr, weekEndStr, weekStart, weekEnd] = getWeekRange(currentWeek);
  const weekNum = getWeekNumber(currentWeek);

  //  Filtro eventi per la settimana (locale-safe, NO hook qui) 
  const isEventInWeek = (ev) => {
    if (!ev?.date) return false;
    let d = null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(ev.date)) {
      const [y, m, dd] = ev.date.split("-").map(Number);
      d = new Date(y, m - 1, dd);
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(ev.date)) {
      const [dd, m, y] = ev.date.split("-").map(Number);
      d = new Date(y, m - 1, dd);
    } else {
      return false;
    }

    d.setHours(0, 0, 0, 0);
    return d >= weekStart && d <= weekEnd;
  };
  const weekEvents = events.filter(isEventInWeek);
  const weekEventsSorted = [...weekEvents].sort((a,b) => evTimeStamp(a) - evTimeStamp(b));

  // LA VISTA FILTRATA PER SQUADRA (E4): una variabile NUOVA, non un filtro
  // «in place» su `weekEventsSorted` (nata quando quella alimentava anche la
  // «My Week», tolta il 1/10/2026). La leggono la tabella di club (i due
  // `<EventsTable>`) e le schede dei totali, così vedono per costruzione lo
  // stesso insieme. Confronto tollerante (maiuscole/spazi) dal lettore unico.
  const eventiDellaVista = filtroSquadra
    ? weekEventsSorted.filter(ev => eDiUnaMiaSquadra(ev.team, [filtroSquadra]))
    : weekEventsSorted;

  // I nomi di squadra dell'anno operativo, dalla lista `teams` già in memoria
  // (nessuna chiamata nuova). Stessa espressione che la modale riceveva in
  // linea; la tendina del filtro ne usa una copia ordinata alfabeticamente
  // (come le opzioni Team di UsersList), la modale l'ordine di sempre.
  const nomiSquadre = Array.isArray(teams)
    ? teams.map(t => t.name || t.id).filter(Boolean)
    : [];
  const opzioniFiltroSquadra = [...nomiSquadre].sort((a, b) => a.localeCompare(b));

  // ⛔️ Qui stavano `squadreDelCappello`, `cappelloDiSquadra` e
  // `myWeekEvents`, le squadre del cappello indossato (fetta C) e gli eventi
  // della «My Week» che ne derivavano: tolti con la My Week (cantiere D7CAL,
  // 1/10/2026, decisione (a) del committente — registro D-07). Con loro
  // `indossaCoach`, che non serviva ad altro. La «settimana personale» vive
  // in MyCalendar.jsx (/my-calendar), non qui.

  // ⛔️ Qui stavano `weekTeams` e `selectableTeams`, le squadre della
  // settimana offerte alle spunte di «Select teams»: tolte con la sezione
  // (cantiere CALENDAR ADMIN, 26/9/2026, decisione 5 del committente).

  // Statistiche rapide — sulla vista filtrata per squadra (E4): con una
  // squadra scelta le schede contano solo lei, come la tabella.
  const stats = { total: 0, home: 0, away: 0, bye: 0 };
  eventiDellaVista.forEach(ev => {
    // Il confronto resta senza maiuscole, come in Outlook (G5-c).
    if ((ev.type || "").toLowerCase() !== "game") return;
    stats.total++;
    const ha = (ev.homeAway || "").toLowerCase();
    if (ha === "bye") stats.bye++;
    else if (ha === "home") stats.home++;
    else if (ha === "away") stats.away++;
  });

  function handleOpenModal(event = null) {
    setEditEvent(event);
    setModalOpen(true);
  }
  function handleCloseModal(reload) {
    setEditEvent(null);
    setModalOpen(false);
    if (reload) loadEvents();
  }
  function toggleModal() {
    if (modalOpen) handleCloseModal(false);
    else handleOpenModal(null);
  }

  //  DELETE EVENT 
  const handleDeleteEvent = async (id) => {
    try {
      await api.del(`/events/${id}`);
      loadEvents();
    } catch (e) {
      alert("Failed to delete event.");
    }
  };

  // ⛔️ Qui stava `exportWeekEventsToCSV`, l'«Export CSV» della settimana
  // generato nel browser: tolto col pulsante (cantiere CALENDAR ADMIN,
  // 26/9/2026, decisione 5 del committente).

  // LA TENDINA «TEAM» (E4). Markup calcato da UsersList (`qpsc-field` >
  // `qpsc-field__label` + `select.qpsc-select`), dentro un contenitore di
  // pagina `ccl-filter` (CalendarClub.css): `.ul-toolbar` è di User List e
  // non si importa. Si monta sopra la tabella di club, nei due rami
  // (telefono e computer). La «My Week», sopra cui non doveva mai stare
  // (regola 5), non c'è più (D7CAL, 1/10/2026).
  const tendinaSquadra = (
    <div className="ccl-filter">
      <div className="qpsc-field">
        <span className="qpsc-field__label">Team</span>
        <select
          className="qpsc-select"
          aria-label="Filter by team"
          value={filtroSquadra}
          onChange={(e) => setFiltroSquadra(e.target.value)}
        >
          <option value="">All Teams</option>
          {opzioniFiltroSquadra.map((team) => (
            <option key={team} value={team}>{team}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <HomeShell backTo="/home" width="admin">
      {/* Telaio comune (doc 07 §7), calco di UsersList.jsx: barra
          utente e banda «‹ Back» / «Switch role» al posto della barra rossa
          (Exit, Back, Export CSV, + Add Event), colonna degli strumenti admin
          a tutta finestra sopra i 768px. Cantiere CALENDAR ADMIN, 26/9/2026,
          decisioni 1-2 del committente. */}

      {/* I PULSANTI IN TESTA (decisione 5): «Full Club Calendar» sale qui
          dalla sezione «Select teams», tolta, accanto a «+ Add Event», sceso
          dalla barra rossa. Gestori identici; entrambi dietro
          `canEditCalendar`, e «Full Club Calendar» non dipende più dagli
          eventi della settimana. Veste e disposizione: calco della riga di
          pulsanti in testa a Uniform Distribution (`qpsc-tool-btn
          qpsc-tool-btn--wide` dentro `.ud-actions`, UniformDistribution.jsx
          e .css §2) — impilati a piena larghezza sotto i 768px, in riga e a
          misura sopra. Scritte a capitalizzazione normale (doc 07 §3.2). */}
      {canEditCalendar && (
        <div className="ccl-actions">
          <button
            type="button"
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            onClick={() => navigate('/outlook')}
          >
            Full Club Calendar
          </button>
          <button
            type="button"
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            onClick={toggleModal}
          >
            + Add Event
          </button>
        </div>
      )}

       {/* RIGA SETTIMANA */}
      <div className="calendar-week-row">
        <button className="calendar-week-arrow" onClick={prevWeek}>&#x25C0;</button>
        <div className="calendar-week-center">
          <span className="calendar-week-dates">{weekStartStr} - {weekEndStr}</span>
          <span className="calendar-week-number">
            Week <span className="weeknum-red">{weekNum}</span>
          </span>
        </div>
        <button className="calendar-week-arrow" onClick={nextWeek}>&#x25B6;</button>
      </div>

      {/* VIEW MOBILE */}
      {mobile && (
        <>
          {/* ⛔️ Qui stava il bivio «cappello PLAYER o COACH (o modalità
              figlio): SOLO MY WEEK», con dentro un secondo «FULL CLUB
              CALENDAR»: tolto (cantiere D7CAL, 1/10/2026, decisione (a) del
              committente — registro D-07, residui 1 e 2). Ogni cappello vede
              ora LA TABELLA DI CLUB, quella che fin qui vedevano solo gli
              altri cappelli (PARENT, ADMIN…).

              ⚠️ IL DIFETTO CHE LA FETTA D AVEVA CHIUSO, e che resta chiuso:
              su telefono il ramo della tabella non c'era, e chi non indossava
              né PLAYER né COACH — il genitore che apre il calendario del
              figlio, per primo — si trovava una PAGINA VUOTA: solo la barra
              dei comandi e la riga della settimana.

              È la stessa riga del desktop (più sotto): stessi eventi, stesso
              permesso — la matita e il cestino restano appesi a
              `canEditCalendar`, quindi chi non è admin la tabella la LEGGE e
              basta.

              Nessun lavoro di grafica sotto l'etichetta «igiene»:
              `EventsTable` porta già la propria resa a schede per il
              telefono (EventsTable.jsx, ramo `isMobile`) e le sue regole
              stanno nel blocco `@media (max-width: 650px)` di
              EventsTable.css.

              Sopra la tabella, la tendina «Team» (E4): sul telefono le
              schede dei totali non ci sono, qui filtra la sola tabella. */}
          {tendinaSquadra}
          <div className="calendar-table-wrapper">
            <EventsTable
              events={eventiDellaVista}
              onEditEvent={handleOpenModal}
              onDeleteEvent={handleDeleteEvent}
              canEditEvents={canEditCalendar}
            />
          </div>

          {/* ⛔️ Qui stavano «Select teams» e, per squadra spuntata, una card
              in linea con matita e cestino: tolti (cantiere CALENDAR ADMIN,
              26/9/2026, decisione 5 del committente). «Full Club Calendar» è
              salito fra i pulsanti in testa. */}
        </>
      )}

      {/* VIEW DESKTOP */}
      {!mobile && (
        <>
          {/* Statistiche di club: tutti tranne chi indossa il cappello PLAYER.
              VISTA (cappello): sono i numeri dell'intero club, non un
              comando. Prima il confronto era `role !== "Player"`, quindi un
              `role: "PLAYER"` maiuscolo le vedeva; ora non più. */}
          {!indossaPlayer && !isChildMode && (
            /* Le schede hanno la grafica di quelle di User List (decisione 4
               del 26/9/2026): classi `ccl-card*`, calco dichiarato di
               `.ul-card*` in CalendarClub.css. Stessi valori, etichette e
               condizione di prima; cadono le quattro barre colorate. */
            <div className="ccl-cards">
              <div className="ccl-card">
                <div className="ccl-card__title">Total Games</div>
                <div className="ccl-card__value">{stats.total}</div>
              </div>
              <div className="ccl-card">
                <div className="ccl-card__title">Home</div>
                <div className="ccl-card__value">{stats.home}</div>
              </div>
              <div className="ccl-card">
                <div className="ccl-card__title">Away</div>
                <div className="ccl-card__value">{stats.away}</div>
              </div>
              <div className="ccl-card">
                <div className="ccl-card__title">Bye</div>
                <div className="ccl-card__value">{stats.bye}</div>
              </div>
            </div>
          )}

          {/* ⛔️ Qui stava il bivio «Tabella eventi, oppure My Week al suo
              posto» per i cappelli PLAYER/COACH (e la modalità figlio), con
              dentro un secondo «FULL CLUB CALENDAR»: tolto (cantiere D7CAL,
              1/10/2026, decisione (a) del committente — registro D-07,
              residui 1 e 2). Ogni cappello vede ora la tabella di club. La
              tendina «Team» (E4) sta fra le schede dei totali e la tabella. */}
          {tendinaSquadra}
          <div className="calendar-table-wrapper">
            <EventsTable
              events={eventiDellaVista}
              onEditEvent={handleOpenModal}
              onDeleteEvent={handleDeleteEvent}
              /* COMANDO: il permesso lo decide il padre, una volta sola,
                 ed è lo stesso dei pulsanti in testa. Prima passava `user.role`
                 grezzo e la tabella decideva da sé con «non è Player»:
                 un genitore vedeva matita e cestino su ogni evento. */
              canEditEvents={canEditCalendar}
            />
          </div>

          {/* ⛔️ Qui stavano «Select teams» e un <WeeklyPlanner> per squadra
              spuntata: tolti (cantiere CALENDAR ADMIN, 26/9/2026, decisione 5
              del committente). «Full Club Calendar» è salito fra i pulsanti in
              testa; `WeeklyPlanner.jsx/.css` non esistono più. */}
        </>
      )}

      {/* MODAL ADD/EDIT */}
      {modalOpen && (
        <EventModal
          show={modalOpen}
          onClose={() => handleCloseModal(false)}
          onSaved={() => handleCloseModal(true)}
          editingEvent={editEvent}
          teamsList={nomiSquadre}
        />
      )}
    </HomeShell>
  );
}